import React, { useState, useEffect, useMemo } from 'react';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import CustomSelect from '../home/collections/CustomSelect';
import { validateNamesForField } from '../../common/utils/validate';
import { TYPE_LABEL_MAP, FIELD_TYPE_OPTIONS } from '../../common/utils/prompt-constants';
import FormFieldWithLabel from '../common/FormFieldWithLabel';

export const PromptDataDisplay = ({
  promptFields = [],
  extractedFields = [],
  onUpdateJsonConfig,
  promptName,
  onSubmit,
  actions,
  showCancel = true,
  onCancel,
  isSavedCollection,
}) => {
  const [promptHeaders, setPromptHeaders] = useState([]);
  const [initialPromptHeaders, setInitialPromptHeaders] = useState([]);
  const [isPromptNameValid, setIsPromptNameValid] = useState(true);
  const [localPromptName, setLocalPromptName] = useState(promptName || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [hasBeenSaved, setHasBeenSaved] = useState(isSavedCollection);

  // STEP 1 — Load initial prompt fields
  useEffect(() => {
    if (Array.isArray(promptFields) && promptFields.length > 0) {
      const updatedFields = promptFields.map((field) => ({
        ...field,
        title: field.title || (field.fieldTitle && field.fieldTitle.en) || '',
        type: field.type || 'text',
        fieldName: field.fieldName || field.title.replace(/\s+/g, '_').toLowerCase(),
      }));
      setPromptHeaders(updatedFields);
      setInitialPromptHeaders(JSON.parse(JSON.stringify(updatedFields)));
    } else {
      setPromptHeaders([]);
      setInitialPromptHeaders([]);
    }
  }, [promptFields]);

  // STEP 2 — Detect changes
  useEffect(() => {
    const changed = () => {
      if (promptHeaders.length !== initialPromptHeaders.length) return true;
      return promptHeaders.some((field, i) => {
        const initialField = initialPromptHeaders[i];
        return (
          field.title !== initialField.title ||
          field.type !== initialField.type
        );
      });
    };
    setHasChanges(changed());
  }, [promptHeaders, initialPromptHeaders]);

  const handleFieldTitleChange = (index, value) => {
    setPromptHeaders((prevHeaders) =>
      prevHeaders.map((field, i) =>
        i === index
          ? {
              ...field,
              title: value,
              fieldTitle: { en: value },
            }
          : field
      )
    );
  };

  const handleDataTypeChange = (index, nameArray, selectedOption) => {
    if (!selectedOption) return;
    setPromptHeaders((prevHeaders) =>
      prevHeaders.map((field, i) =>
        i === index ? { ...field, type: selectedOption.value } : field
      )
    );
  };

  const handleDeleteField = (index) => {
    setPromptHeaders((prevHeaders) => prevHeaders.filter((_, i) => i !== index));
  };

  const handleAddField = () => {
    const newField = {
      title: '',
      fieldTitle: { en: '' },
      fieldName: 'field_' + Date.now(),
      type: 'text',
    };
    setPromptHeaders((prevHeaders) => [...prevHeaders, newField]);
  };

  const validatePromptName = (name) => /^[a-zA-Z\s]+$/.test(name);

  // STEP 3 — SAVE using fieldName as stable identifier
  const handleSave = async () => {
    if (!localPromptName || !validatePromptName(localPromptName)) {
      setIsPromptNameValid(false);
      return;
    }

    const flatExtracted = extractedFields ? extractedFields.flat() : [];

    const updatedFields = promptHeaders.map((field) => {
      const title = field.title;
      const type = field.type;
      const fieldName = field.fieldName;

      // MATCH FIELD BY fieldName (stable key)
      const existingField = flatExtracted.find((f) => f.fieldName === fieldName);

      let updatedField;

      if (existingField) {
        updatedField = { ...existingField };

        updatedField.fieldTitle = { en: title };
        updatedField.placeholder = { en: title };
        updatedField.fieldName = fieldName;
        updatedField.type = type;

        // Keep static options when type is static_option
        if (type === 'static_option') {
          if (!updatedField.staticOptions) updatedField.staticOptions = [];
        }

        // Keep reference link intact
        if (existingField.refCollection) {
          updatedField.refCollection = existingField.refCollection;
        }
      } else {
        // Create a completely new field
        updatedField = {
          fieldName,
          fieldTitle: { en: title },
          placeholder: { en: title },
          type: type,
          staticOptions: type === 'static_option' ? [] : [],
          refCollection: {
            collectionName: '',
            collectionField: '',
            parentCollectionField: '',
            displayType: 'dropdown',
            isShowAsText: false,
            isMultiSelect: false,
          },
        };
      }

      return updatedField;
    });

    const updatedJsonConfig = {
      collectionTitle: localPromptName,
      fields: [updatedFields],
    };

    onUpdateJsonConfig(updatedJsonConfig);

    try {
      await onSubmit(updatedJsonConfig);
      setInitialPromptHeaders(JSON.parse(JSON.stringify(promptHeaders)));
      setHasChanges(false);
      setHasBeenSaved(true);
      if (onCancel) onCancel();
    } catch (err) {
      console.error('SAVE ERROR:', err);
    }
  };

  // Create formik init
  const initialValues = useMemo(() => {
    return promptHeaders.reduce((acc, field, index) => {
      acc[`header_${index}`] = field.title || '';
      acc[`type_${index}`] = field.type || 'text';
      return acc;
    }, {});
  }, [promptHeaders]);

  const getTypeLabel = (type) => {
    if (TYPE_LABEL_MAP[type]) return TYPE_LABEL_MAP[type];
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
  };

  const isSubmitDisabled = () => {
    if (!hasBeenSaved) return false;
    if (hasChanges) return false;
    return true;
  };

  return (
    <div className="container">
      {/* Collection Name */}
      <div className="form-group">
        <FormFieldWithLabel label="Collection Name" cta={false} isRequired icon="text">
          <input
            type="text"
            className="form-control form-control-sm mt-2"
            value={localPromptName}
            onChange={(e) => {
              setLocalPromptName(e.target.value);
              setIsPromptNameValid(true);
              setHasChanges(true);
            }}
            disabled={hasBeenSaved}
          />
        </FormFieldWithLabel>
      </div>

      {/* Fields */}
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSave}>
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            {promptHeaders.map((field, index) => (
              <div key={index} className="form-row mb-2">
                {/* Field Title */}
                <div className="col-sm-6">
                  <Field
                    type="text"
                    name={`header_${index}`}
                    className="form-control form-control-sm"
                    value={values[`header_${index}`] || ''}
                    onChange={(e) => {
                      handleChange(e);
                      handleFieldTitleChange(index, e.target.value);
                    }}
                    validate={(value) =>
                      value ? validateNamesForField(value) : 'Field name required'
                    }
                  />
                  <ErrorMessage
                    name={`header_${index}`}
                    component="div"
                    className="text-danger"
                  />
                </div>

                {/* Field Type */}
                <div className="col-sm-5">
                  <CustomSelect
                    name={`type_${index}`}
                    isMulti={false}
                    options={FIELD_TYPE_OPTIONS}
                    onChange={(name, selectedOption) => {
                      handleDataTypeChange(index, name, selectedOption);
                      setFieldValue(`type_${index}`, selectedOption.value);
                    }}
                    value={{
                      label: getTypeLabel(field.type),
                      value: field.type,
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-xs btn-theme-delete btn-icon-only ml-1"
                  onClick={() => handleDeleteField(index)}
                  disabled={field.type === 'reference'}
                >
                  <i className="wb webconnect-times"></i>
                </button>
              </div>
            ))}

            <div className="d-flex justify-content-end mt-3">
              <button type="button" className="btn btn-theme-submit btn-sm" onClick={handleAddField}>
                <i className="wb webconnect-plus"></i> Add Field
              </button>
            </div>

            <div className="d-flex justify-content-between mt-3">
              <button
                type="button"
                className={`btn btn-outline-secondary btn-sm ${showCancel ? '' : 'invisible'}`}
                onClick={onCancel}
              >
                Cancel
              </button>

              <button
                type="submit"
                className={`btn btn-sm ${
                  isSubmitDisabled() ? 'btn-default' : 'btn-theme-submit'
                }`}
                disabled={isSubmitDisabled()}
              >
                <i className="wb webconnect-save-regular"></i>&nbsp;
                {hasBeenSaved ? 'Update Collection' : 'Create Collection'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};